import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { formatCalendarDay } from 'support/helpers/dateTime/dateTime';

import { Summary } from '../types';

type DispatchInstructionSummaryValues = {
  totalQuantity: number;
  lineCount: number;
  deliveryDate?: number;
};

export const useDispatchInstructionSummary = (): Summary => {
  const { t } = useTranslation();
  const values = useDispatchInstructionSummaryValues();

  return useMemo(
    () => ({
      content: [
        [
          {
            label: t('webedi:documentSummary.dispatchInstruction.totalQuantity.label'),
            value: Math.round(values.totalQuantity),
          },
          {
            label: t('webedi:documentSummary.dispatchInstruction.lineCount.label'),
            value: Math.round(values.lineCount),
          },
          {
            label: t('webedi:documentSummary.dispatchInstruction.deliveryDate.label'),
            value: formatCalendarDay(values.deliveryDate) || '—',
          },
        ],
      ],
    }),
    [t, values],
  );
};

const calculateLineItemsQuantity = (lineItems: Array<any> | undefined): number => {
  return (
    lineItems?.reduce((acc, item) => {
      if (item.line_items?.length) {
        return acc + calculateLineItemsQuantity(item.line_items);
      }

      return acc + (item.dispatch_quantity ?? 0);
    }, 0) || 0
  );
};

const useDispatchInstructionSummaryValues = (): DispatchInstructionSummaryValues => {
  const [lineItems, requestedDeliveryDate] = useWatch({
    name: ['line_items', 'requested_delivery_date'],
  });

  return useMemo(
    () => ({
      totalQuantity: calculateLineItemsQuantity(lineItems),
      lineCount: lineItems?.length ?? 0,
      deliveryDate: requestedDeliveryDate,
    }),
    [lineItems, requestedDeliveryDate],
  );
};
